import $ from 'jquery';

import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';
import deparam from 'lib/vendor/jquery-deparam-patched';

import type { ServiceAvailabilityData } from './Tc.Module.ServiceAvailability';
import { OrderButtonsInitData } from './Tc.Module.OrderOverlayOrderButtons';

export interface OpenOverlayData {
	orderButtonsId : string;
	orderLink : string;
	orderLinkNoLogin : string;
	productIds? : {
		name : string;
		siebelId : string;
	};
	productId? : string;
	productName : string;
}

/**
 * Order Overlay for MAP Bundle Order or Lego Container pages.
 *
 * @author Michael Keller
 * @namespace Tc.Module
 * @class OrderOverlay
 * @extends Tc.Module
 */
export default class OrderOverlay extends Module {
	private isMobile = false;
	private mobilePageId = 'orderoverlay';

	private $orderButton! : JQuery;
	private $mobileButtonHolder! : JQuery;

	// Can be set via fire('openOrderOverlay'), and will then override button attributes
	private orderLink : string | null = null;
	private orderLinkNoLogin : string | null = null;
	private registrationLink : string | null = null;

	public mobilePageHelper! : SCS.MobilePageHelper;

	/**
	 * Initializes OrderOverlay
	 *
	 * @method on
	 * @param {Function} callback function
	 * @return void
	 */
	public on(callback : Hook) {
		this.isMobile = $('body').is('.is-mobile');
		this.$orderButton = $('.scs-product-orderbutton-buy, .is-mobile .order-button');
		this.$mobileButtonHolder = this.$ctx.find('.orderoverlay-mobile-button-holder');

		// Move error messages of service availability module before button - design differs from serviceavailability when in overlay
		this.$ctx.find('.modScForm > form > .button').before(this.$ctx.find('.modScForm > form > .error'));
		this.$ctx.find('.modScForm > form > .error').css('clear', 'left');

		this.$ctx.find('.limited-offers').hide();
		this.$ctx.find('.no-offers').hide();

		// Mobile initialization (uses jquery-mobile "page")
		if (this.isMobile) {
			this.mobilePageHelper = new SCS.MobilePageHelper('body');
			this.$ctx.find('a.back').text('< ' + this.$ctx.data('buttonBack'));

			// Listen for pagechange event to a page with the id == mod.mobilePageId
			$(document).on('pagechange', (event, data) => {
				if (data && data.toPage && data.toPage.filter('#' + this.mobilePageId).length === 1) {
					this.fire('dialogOpened');
				}
				this.log(event, data);
			});

			if ($('.modPriceOrderContainer, .modLegoOrderButton').length === 0) {
				// Do this only for "old" pages (e.g. BundleVariations);
				// The new ones open the overlay via onOpenOverlay and supply the needed data there - buttons are generated there
				// For mobile, move the existing buttons into orderoverlay-mobile-button-holder (only visible after availability-check)
				// and add a "check availability"-button into the page instead
				this.$ctx.parent().siblings('.order-button, .shop-finder.secondary-button').appendTo('.orderoverlay-mobile-button-holder');
				this.$ctx.parent().before('<a class="button primary-button check-availability-button"></a>');

				// Reset mod.$orderButton to the newly created "Check Availability"-button
				this.$orderButton = this.$ctx.parent().prev('.check-availability-button');
				this.$orderButton.text(this.$ctx.data('translation-check-availability'));
			}

		}

		// Listen for clicks on order-button
		this.$orderButton.click(e => {
			if (!this.$ctx.is('.cq_isEditMode *')) {
				if (this.isMobile) {
					this.openInMobilePage();
				} else {
					this.openOverlay();
				}
			}
			e.preventDefault();
		});

		// Close overlay, when cancel-button is clicked
		this.$ctx.find('.modServiceAvailability .cancel-button').on('click', () => {
			if (this.isMobile) {
				// Detach the created mobile page-overlay to prevent issues with navigation flyout and repeated opening of the overlay.
				// timeout prevents a blank screen before switching back to the actual page content.
				setTimeout(function() { $('#orderoverlay').detach(); }, 500);
				window.history.back(); // Switch back to main page (close "overlay")
			} else {
				this.$ctx.dialog('close');
			}
		});

		callback();
	}

	public openOverlay() {
		this.$ctx.dialog({
			classes: {'ui-dialog': 'order-overlay-dialog sdx-container'},
			close: () => {
				this.fire('dialogClosed');
			},
			open: () => {
				const data : OrderButtonsInitData = {
					href: this.orderLink ?? this.$orderButton.attr('href'),
					noLoginUrl: this.orderLinkNoLogin ?? this.$orderButton.data('no-login-url'),
					registrationUrl: this.registrationLink,
				};
				this.fire('orderButtonsInit', data);
				this.fire('dialogOpened');
			},
			width: 780,
		});
	}

	public openInMobilePage() {
		SCS.createNewMobilePage({content: this.$ctx, cssClass: 'ui-body-c content-fw'});

		const data : OrderButtonsInitData = {
			href: this.orderLink ? this.orderLink : this.$orderButton.attr('href'),
			noLoginUrl: this.orderLinkNoLogin ? this.orderLinkNoLogin : this.$orderButton.data('no-login-url'),
			registrationUrl: this.registrationLink,
		};
		this.fire('orderButtonsInit', data);

		this.$ctx.closest('.ui-content').css('overflow-x', 'visible');
		this.sandbox.addModules($('.modHeader'));
	}

	public showMobileOrderButtons() {
		this.$mobileButtonHolder.removeClass('hidden');
		this.onCenterOverlay();
	}

	public hideMobileOrderButtons() {
		this.$mobileButtonHolder.addClass('hidden');
		this.onCenterOverlay();
	}

	public isActive() {
		return this.$ctx.is(':visible') || this.isMobile && this.$ctx.is('#orderoverlay *');
	}

	/**
	 * Module communication
	 */
	public onCenterOverlay() {
		const mod = this;

		if (mod.$ctx.is('.ui-dialog-content')) {
			mod.log('Tc.Module.OrderOverlay > centerOverlay');
			mod.$ctx.dialog('reposition');
		}
	}

	public onServiceAvailabilityDataUpdate(response : ServiceAvailabilityData) {
		const mod = this;
		if (!mod.isActive()) {
			return;
		}

		// FIXME: Why don’t the types match?
		const data = response as any;

		// React to result of service availability request
		mod.log('Tc.Module.OrderOverlay > received new serviceavailability data', data);

		// Show orderbuttons whenever the product is available or restricted => can be ordered
		// in this case - hide the additional texts
		if ((data?.productAvailability?.length ?? 0) > 0 && data.productAvailability[0].availability !== 'not_available') {
			// Product available or restricted
			mod.fire('orderButtonsShow');
			mod.showMobileOrderButtons();
		} else if ((data?.productAvailability?.length ?? 0) > 0 && data.productAvailability[0].availability === 'not_available') {
			// Product not available
			mod.hideMobileOrderButtons();
			if (data.tvAvailability === 'available' || data.tvAvailability === 'restricted') {
				mod.$ctx.find('.limited-offers').show();
			} else {
				mod.$ctx.find('.no-offers').show();
			}
		}

		// Hide no-login link for checks based on phone number and show a different button
		// FIXME: Remove since type never changes anymore
		if (!mod.isMobile) {
			mod.fire('checkTypeChange', data.requestType);
		} else {
			mod.$ctx.find('.order-link-new-customer-mobile').toggleClass('hidden', data.requestType === 'phone');
		}

		// Update product id
		if (data && data.productAvailability && data.productAvailability.length > 0) {
			mod.fire('UpdateProductId', {
				name: data.productAvailability[0].name,
				siebelId: data.productAvailability[0].siebelId,
			});
		}

		mod.onCenterOverlay();
	}

	public onServiceAvailabilityStatusUpdate(data : 'form' | 'result') {
		const mod = this;
		if (!mod.isActive()) {
			return;
		}

		// Hide other modules in overlay anyway - will be displayed depending on what is delivered to onServiceAvailabilityDataUpdate
		mod.fire('orderButtonsHide');
		mod.$ctx.find('.limited-offers, .no-offers').hide();

		if (data === 'form') {
			mod.$ctx.find('.form-title').removeClass('hidden');
			mod.hideMobileOrderButtons();
		} else {
			mod.$ctx.find('.form-title').addClass('hidden');
		}
		mod.onCenterOverlay();
	}

	/**
	 * Listens to modPriceOrderContainer and modLegoOrderButton
	 */
	public onOpenOverlay(data : OpenOverlayData) {
		const samLink = this.$ctx.data('sam-link');
		const orderLinkMatchQueryString = data.orderLink.match(/[?#](.*)/) || [];
		const orderLinkQueryString = orderLinkMatchQueryString[1] || '';
		const orderLinkParams = deparam(orderLinkQueryString);

		this.log('onOpenOverlay', data);

		this.orderLink = data.orderLink;
		this.orderLinkNoLogin = data.orderLinkNoLogin;

		this.registrationLink = SCS.Url.updateQueryStringParameter(samLink, 'lang', orderLinkParams.lang);
		this.registrationLink = SCS.Url.updateQueryStringParameter(this.registrationLink, 'serviceName', 'consult');
		this.registrationLink = SCS.Url.updateQueryStringParameter(this.registrationLink, 'RURL', encodeURIComponent(data.orderLink));

		if (data.productIds) {
			this.fire('UpdateProductIds', {
				name: data.productName,
				productIds: data.productIds,
			});
		} else {
			this.fire('UpdateProductId', {
				name: data.productName || data.productId,
				siebelId: data.productId,
			});
		}

		if (this.isMobile) {
			this.$mobileButtonHolder.empty();
			$('#' + data.orderButtonsId).clone().removeClass('hidden').appendTo(this.$mobileButtonHolder);
			this.addSiteCatalystTracking();

			this.openInMobilePage();
		} else {
			this.openOverlay();
		}
	}

	/**
	 * Track the mobile buttons (which are not handled in OrderOverlayOrderButtons).
	 * Handles the buttons provided by the Lego Order Button component.
	 */
	public addSiteCatalystTracking() {
		const win = window as any;

		this.$mobileButtonHolder.find('.order-link-existing-customer-mobile').click(event => {
			const siteCatalyst = win.s || win.s_one;
			if (siteCatalyst && siteCatalyst.tl) {
				siteCatalyst.tl(event.target, 'o', 'OrderOverlay_OrderButton_WithLogin');
			}
		});

		this.$mobileButtonHolder.find('.order-link-new-customer-mobile').click(event => {
			const siteCatalyst = win.s || win.s_one;
			if (siteCatalyst && siteCatalyst.tl) {
				siteCatalyst.tl(event.target, 'o', 'OrderOverlay_OrderButton_WithoutLogin');
			}
		});
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Tc.Module.OrderOverlay.ts